import { db } from "../storage/db";
import { SCHEMA_VERSION } from "../domain/types";
import { now } from "../domain/rules";

export type GatingConfig = {
  enabled: boolean;
  licenseServerUrl: string;
  manualLicenseCodeEnabled: boolean;
  stripeCheckoutEnabled: boolean;
  digistoreMagicLinkEnabled: boolean;
  launchOfferEnabled: boolean;
  launchOfferTag: string;
  manualCheckoutUrl: string;
  supportUrl: string;
  requestTimeoutMs: number;
};

export type StoredLicenseActivation = {
  activationId: string;
  activationToken: string;
  licenseFingerprint: string;
  keySuffix: string;
  validatedAt: string;
  gracePeriodDays: number;
};

export type LicenseAccessState = {
  checking: boolean;
  hasAccess: boolean;
  mode: "disabled" | "licensed" | "grace" | "locked";
  activation?: StoredLicenseActivation;
  message?: string;
};

type RawLicenseActivation = Partial<StoredLicenseActivation> & {
  activation_id?: string;
  activation_token?: string;
  token?: string;
  license_fingerprint?: string;
  key_suffix?: string;
  validated_at?: string;
  grace_period_days?: number;
};

type ActivationResponse = {
  ok?: boolean;
  activation?: RawLicenseActivation;
  error?: string;
  message?: string;
};

const ACTIVATION_ID_KEY = "ai-act-license-activation-id";
const ACTIVATION_KEY = "ai-act-license-activation";
const ACTIVATION_CACHE_ID = "license_activation_current";
const LICENSE_RECHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const disabledLicenseAccess: LicenseAccessState = {
  checking: false,
  hasAccess: true,
  mode: "disabled"
};

export const lockedLicenseAccess: LicenseAccessState = {
  checking: false,
  hasAccess: false,
  mode: "locked"
};

export function getOrCreateActivationId() {
  const existing = localStorage.getItem(ACTIVATION_ID_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(ACTIVATION_ID_KEY, id);
  return id;
}

export function readStoredActivation(): StoredLicenseActivation | undefined {
  const raw = localStorage.getItem(ACTIVATION_KEY);
  if (!raw) return undefined;
  try {
    return normalizeActivation(JSON.parse(raw) as RawLicenseActivation);
  } catch {
    return undefined;
  }
}

export async function readCachedActivation(): Promise<StoredLicenseActivation | undefined> {
  const localActivation = readStoredActivation();
  if (localActivation) return localActivation;

  const sqliteActivation = await readSqliteActivation();
  if (sqliteActivation) {
    storeActivationLocal(sqliteActivation);
    return sqliteActivation;
  }

  return undefined;
}

export async function storeActivation(activation: StoredLicenseActivation) {
  storeActivationLocal(activation);
  await storeSqliteActivation(activation);
}

export async function clearActivation() {
  localStorage.removeItem(ACTIVATION_KEY);
  await clearSqliteActivation();
}

export function getLicenseQuery(search: string) {
  const params = new URLSearchParams(search);
  return {
    autoUnlockToken: params.get("auto_unlock") ?? "",
    checkout: params.get("checkout") ?? "",
    sessionId: params.get("session_id") ?? ""
  };
}

export function removeLicenseQueryParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete("auto_unlock");
  url.searchParams.delete("checkout");
  url.searchParams.delete("session_id");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", next);
}

export function gracePeriodValid(activation: StoredLicenseActivation, nowMs = Date.now()) {
  const validatedAt = Date.parse(activation.validatedAt);
  if (!Number.isFinite(validatedAt)) return false;
  const graceMs = Math.max(0, activation.gracePeriodDays) * 24 * 60 * 60 * 1000;
  return validatedAt + graceMs >= nowMs;
}

export function licenseCheckFresh(activation: StoredLicenseActivation, nowMs = Date.now()) {
  const validatedAt = Date.parse(activation.validatedAt);
  if (!Number.isFinite(validatedAt)) return false;
  return nowMs - validatedAt >= 0 && nowMs - validatedAt < LICENSE_RECHECK_INTERVAL_MS;
}

export async function activateLicenseCode(config: GatingConfig, licenseCode: string) {
  const activationId = getOrCreateActivationId();
  const data = await requestLicense<ActivationResponse>(config, "/api/license/activate", {
    method: "POST",
    body: { licenseCode, activationId }
  });
  return requireActivation(data);
}

export async function redeemAutoUnlock(config: GatingConfig, token: string) {
  const activationId = getOrCreateActivationId();
  const data = await requestLicense<ActivationResponse>(config, "/api/license/redeem-auto-unlock", {
    method: "POST",
    body: { token, activationId }
  });
  return requireActivation(data);
}

export async function validateStoredActivation(config: GatingConfig, options: { forceRemote?: boolean } = {}) {
  const activation = await readCachedActivation();
  if (!activation) return { ...lockedLicenseAccess, message: "Noch keine Lizenz aktiviert." };
  if (!options.forceRemote && licenseCheckFresh(activation)) {
    return {
      checking: false,
      hasAccess: true,
      mode: "licensed",
      activation
    } satisfies LicenseAccessState;
  }
  try {
    const data = await requestLicense<ActivationResponse>(config, "/api/license/status", {
      method: "POST",
      body: {
        activationId: activation.activationId,
        activationToken: activation.activationToken
      }
    });
    const nextActivation = data.activation ? mergeActivation(activation, data.activation) : { ...activation, validatedAt: new Date().toISOString() };
    await storeActivation(nextActivation);
    return {
      checking: false,
      hasAccess: true,
      mode: "licensed",
      activation: nextActivation
    } satisfies LicenseAccessState;
  } catch (error) {
    if (isTechnicalFailure(error) && gracePeriodValid(activation)) {
      return {
        checking: false,
        hasAccess: true,
        mode: "grace",
        activation,
        message: "Lizenzserver aktuell nicht erreichbar. Offline-Grace-Period aktiv."
      } satisfies LicenseAccessState;
    }
    await clearActivation();
    return {
      checking: false,
      hasAccess: false,
      mode: "locked",
      message: licenseErrorMessage(error)
    } satisfies LicenseAccessState;
  }
}

export async function startStripeCheckout(config: GatingConfig, customerEmail: string) {
  const data = await requestLicense<{ ok?: boolean; url?: string; error?: string; message?: string }>(
    config,
    "/api/stripe/create-checkout-session",
    {
      method: "POST",
      body: {
        ...(customerEmail ? { customerEmail } : {}),
        ...(config.launchOfferEnabled && config.launchOfferTag ? { offer: config.launchOfferTag } : {})
      }
    }
  );
  if (!data.ok || !data.url) throw new Error(data.error ?? data.message ?? "Stripe Checkout konnte nicht gestartet werden.");
  window.location.assign(data.url);
}

export async function completeStripeCheckout(config: GatingConfig, sessionId: string) {
  const activationId = getOrCreateActivationId();
  const params = new URLSearchParams({ session_id: sessionId, activation_id: activationId });
  const data = await requestLicense<ActivationResponse>(config, `/api/stripe/checkout-success?${params.toString()}`, {
    method: "GET"
  });
  return requireActivation(data);
}

export function licenseErrorMessage(error: unknown) {
  if (!(error instanceof Error)) return "Lizenzprüfung fehlgeschlagen.";
  const code = error.message;
  const known: Record<string, string> = {
    token_not_found: "Der Freischalt-Link ist unbekannt. Bitte Lizenzcode manuell eingeben.",
    token_expired: "Der Freischalt-Link ist abgelaufen. Bitte Lizenzcode manuell eingeben.",
    token_consumed: "Der Freischalt-Link wurde bereits verwendet. Bitte Lizenzcode manuell eingeben.",
    activation_limit: "Das Aktivierungslimit ist erreicht. Bitte Support kontaktieren.",
    license_not_active: "Diese Lizenz ist nicht aktiv.",
    revoked: "Diese Lizenz wurde gesperrt.",
    refunded: "Diese Lizenz wurde erstattet und ist nicht mehr aktiv.",
    chargeback: "Diese Lizenz ist wegen Chargeback nicht mehr aktiv.",
    expired: "Diese Lizenz ist abgelaufen."
  };
  return known[code] ?? code;
}

async function requireActivation(data: ActivationResponse) {
  if (!data.ok || !data.activation) throw new Error(data.error ?? data.message ?? "Lizenzaktivierung fehlgeschlagen.");
  const activation = normalizeActivation(data.activation);
  await storeActivation(activation);
  return activation;
}

function storeActivationLocal(activation: StoredLicenseActivation) {
  localStorage.setItem(ACTIVATION_ID_KEY, activation.activationId);
  localStorage.setItem(ACTIVATION_KEY, JSON.stringify(activation));
}

async function readSqliteActivation(): Promise<StoredLicenseActivation | undefined> {
  try {
    const cached = await db.licenseActivations.get(ACTIVATION_CACHE_ID);
    return cached ? normalizeActivation(cached) : undefined;
  } catch {
    return undefined;
  }
}

async function storeSqliteActivation(activation: StoredLicenseActivation) {
  try {
    const existing = await db.licenseActivations.get(ACTIVATION_CACHE_ID);
    const timestamp = now();
    await db.licenseActivations.put({
      ...activation,
      id: ACTIVATION_CACHE_ID,
      schema_version: SCHEMA_VERSION,
      created_at: existing?.created_at ?? timestamp,
      updated_at: timestamp
    });
  } catch {
    // localStorage remains the compatibility fallback when SQLite/OPFS is unavailable.
  }
}

async function clearSqliteActivation() {
  try {
    await db.licenseActivations.delete(ACTIVATION_CACHE_ID);
  } catch {
    // Ignore unavailable local database during explicit license reset.
  }
}
function mergeActivation(current: StoredLicenseActivation, update: RawLicenseActivation): StoredLicenseActivation {
  return normalizeActivation({
    ...current,
    ...update,
    activationToken: update.activationToken ?? update.activation_token ?? update.token ?? current.activationToken,
    activation_token: update.activation_token,
    token: update.token
  });
}
function normalizeActivation(activation: RawLicenseActivation): StoredLicenseActivation {
  const normalized = {
    activationId: activation.activationId ?? activation.activation_id ?? "",
    activationToken: activation.activationToken ?? activation.activation_token ?? activation.token ?? "",
    licenseFingerprint: activation.licenseFingerprint ?? activation.license_fingerprint ?? "",
    keySuffix: activation.keySuffix ?? activation.key_suffix ?? "",
    validatedAt: activation.validatedAt ?? activation.validated_at ?? new Date().toISOString(),
    gracePeriodDays: Number(activation.gracePeriodDays ?? activation.grace_period_days ?? 0)
  };
  if (!normalized.activationId || !normalized.activationToken || !normalized.validatedAt) {
    throw new Error("Lizenzaktivierung unvollstaendig.");
  }
  return normalized;
}

async function requestLicense<T>(
  config: GatingConfig,
  path: string,
  options: { method: "GET" | "POST"; body?: unknown }
): Promise<T> {
  if (!config.licenseServerUrl) throw new Error("Lizenzserver-URL fehlt in settings.yaml.");
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), config.requestTimeoutMs);
  try {
    const response = await fetch(`${config.licenseServerUrl.replace(/\/$/, "")}${path}`, {
      method: options.method,
      headers: options.body ? { "Content-Type": "application/json" } : undefined,
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) {
      const error = new Error(data.error ?? data.message ?? `license_http_${response.status}`);
      if (response.status >= 500) {
        error.name = "TechnicalLicenseFailure";
      }
      throw error;
    }
    return data as T;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      const timeoutError = new Error("Lizenzserver nicht erreichbar.");
      timeoutError.name = "TechnicalLicenseFailure";
      throw timeoutError;
    }
    if (error instanceof TypeError) {
      const networkError = new Error("Lizenzserver nicht erreichbar.");
      networkError.name = "TechnicalLicenseFailure";
      throw networkError;
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

function isTechnicalFailure(error: unknown) {
  return error instanceof Error && error.name === "TechnicalLicenseFailure";
}
